import React from 'react';
import { RightOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { Search } from '../../../types';
import { summarizePipeline } from '../searchPipeline';
import css from './PipelineChip.module.scss';

interface PipelineChipProps {
  search: Search;
  onOpen: () => void;
}

/**
 * Compact, persistent progress affordance on a search card — a doorway into the
 * full step list (SearchProgressDrawer), not a passive pill. Backend-driven, so
 * it is correct on load/refresh. Hidden when there's nothing useful to show (no
 * pipeline, complete, or paused — those are carried by the card's status badge).
 *
 * Reads as a control: a stage label, a segmented step track (one segment per
 * real stage, filled to the current step — the live phase's tone), and a
 * trailing chevron that slides on hover. `running` is green (first run in
 * flight); `recrawling` is a quieter slate (daily refresh).
 */
const PipelineChip: React.FC<PipelineChipProps> = ({ search, onOpen }) => {
  const summary = summarizePipeline(search);
  if (!summary || summary.phase === 'complete' || summary.phase === 'paused') return null;

  const stages = search.pipeline?.stages ?? [];
  const total = stages.length;
  const activeIdx = stages.findIndex((s) => s.state === 'active');
  const currentStep = activeIdx >= 0 ? activeIdx + 1 : summary.progress.done;

  return (
    <Tooltip title={`Step ${currentStep} of ${total} · View all steps`}>
      <button
        type="button"
        className={`${css.chip} ${css[summary.phase]}`}
        onClick={onOpen}
        aria-label={`${summary.chipLabel} — step ${currentStep} of ${total}. View all steps.`}
      >
        <span className={css.label}>{summary.chipLabel}</span>
        <span className={css.track} aria-hidden="true">
          {stages.map((s) => (
            <span key={s.key} className={`${css.seg} ${css[`seg_${s.state}`]}`} />
          ))}
        </span>
        <RightOutlined className={css.chevron} aria-hidden="true" />
      </button>
    </Tooltip>
  );
};

export default PipelineChip;
